/* Softhour commerce. Shopify remains the authority for variants, availability and totals. */
(function (factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (typeof window !== 'undefined' && !window.SofthourCommerce) {
    window.SofthourCommerce = api;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => api.mount(window), { once: true });
    else api.mount(window);
  }
})(function () {
  'use strict';
  const DEFAULTS = {
    addToCart: 'Add to bag', adding: 'Adding…', soldOut: 'Sold out', unavailable: 'Choose another combination',
    comingSoon: 'Coming soon', invalidQuantity: 'Choose a valid quantity.', loading: 'Updating…',
    cartError: 'We couldn’t update your bag. Please try again.',
    cartReadError: 'We couldn’t load your bag. Open the bag page to check your items.',
    uncertain: 'Your update may still be processing. New bag changes are paused to prevent duplicates. Review your bag; do not add the same item again.',
    reviewNeeded: 'Your last update is still unconfirmed. New bag changes are paused to prevent duplicates. Open your bag to review it.',
    reviewBag: 'Review bag',
    acceptedUnread: 'Your item was added, but we couldn’t reload your bag. Open your bag to check it before adding again.',
    missingLine: 'This item has changed in your bag. Please review your bag and try again.',
    free: 'FREE', remove: 'Remove', quantity: 'Quantity', decrease: 'Decrease quantity', increase: 'Increase quantity',
    save: 'Save', unavailablePrice: '—', plan: 'Plan', off: 'OFF'
  };
  function route(root, endpoint) {
    const clean = String(root || '/').replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0];
    const path = '/' + clean.replace(/^\/+|\/+$/g, '');
    return (path === '/' ? '/' : path + '/') + endpoint.replace(/^\/+/, '');
  }
  function parseQuantity(value, rules) {
    const r = rules || {};
    const str = String(value == null ? '' : value).trim();
    if (!/^\d+$/.test(str)) return null;
    const n = Number(str), min = Number(r.min == null ? 1 : r.min), max = Number(r.max), step = Number(r.step || 1);
    if (!Number.isSafeInteger(n) || n < min || (Number.isFinite(max) && r.max !== '' && r.max != null && n > max)) return null;
    if (step > 0 && (n - min) % step !== 0) return null;
    return n;
  }
  function variantOptions(variant) {
    return Array.isArray(variant.options) ? variant.options.map(String) : [variant.option1, variant.option2, variant.option3].filter(v => v != null).map(String);
  }
  function resolveVariant(variants, options) {
    return variants.find(v => {
      const values = variantOptions(v);
      return values.length === options.length && values.every((value, index) => value === String(options[index]));
    }) || null;
  }
  function priceState(variant, quantity) {
    if (!variant || !Number.isFinite(Number(variant.price)) || quantity == null) return null;
    const unit = Number(variant.price), total = unit * quantity;
    const reference = Number(variant.compare_at_price || 0) * quantity;
    return { unit, total, compare: reference > total ? reference : null, saving: Math.max(0, reference - total) };
  }
  function cartItems(entries) {
    if (!Array.isArray(entries) || !entries.length) return null;
    const grouped = new Map();
    for (const entry of entries) {
      if (!entry || !/^\d+$/.test(String(entry.id)) || parseQuantity(entry.quantity) == null) return null;
      const item = Object.assign({}, entry, { id: String(entry.id), quantity: Number(entry.quantity) });
      const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
      const key = JSON.stringify([item.id, item.selling_plan || null, properties]);
      const existing = grouped.get(key);
      if (existing) {
        existing.quantity += item.quantity;
        if (!Number.isSafeInteger(existing.quantity)) return null;
      } else grouped.set(key, item);
    }
    return Array.from(grouped.values());
  }
  function money(cents, currency, locale) {
    try { return new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' }).format(Number(cents) / 100); }
    catch (_) { return (Number(cents) / 100).toFixed(2) + ' ' + (currency || 'USD'); }
  }
  function explicitMoney(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
  }
  function linePriceState(item) {
    const final = explicitMoney(item.final_line_price ?? item.line_price);
    const original = explicitMoney(item.original_line_price);
    const allocations = (Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : []).filter(allocation => explicitMoney(allocation?.amount) > 0);
    const allocated = allocations.reduce((sum, allocation) => sum + allocation.amount, 0);
    const discounted = final !== null && original !== null && original > final && allocated === original - final;
    const percent = discounted ? Math.round((original - final) * 100 / original) : 0;
    return { final, original, free: final === 0, discounted, percent, allocations };
  }
  function cartLinePriceState(item, catalog, currency) {
    const native = linePriceState(item);
    const quantity = parseQuantity(item.quantity);
    const variant = catalog?.currency === currency && String(item.product_id) === String(catalog.productId) && !item.selling_plan_allocation
      ? catalog.variants?.find(v => String(v.id) === String(item.variant_id)) : null;
    const compare = explicitMoney(variant?.compare), unit = explicitMoney(variant?.price);
    const reference = quantity !== null && compare !== null && unit !== null && compare > unit && explicitMoney(item.original_price) === unit && native.original === unit * quantity ? compare * quantity : null;
    if (reference === null || !Number.isSafeInteger(reference) || native.final === null || native.final > native.original) return { ...native, saving: native.discounted ? native.original - native.final : 0 };
    return { ...native, original: reference, discounted: reference > native.final, saving: reference - native.final, percent: 0 };
  }
  function cartPriceSummary(cart, catalog, currency) {
    const total = explicitMoney(cart.total_price);
    const reference = cart.items.reduce((sum, item) => {
      const price = cartLinePriceState(item, catalog, currency);
      return sum === null || price.original === null ? null : sum + price.original;
    }, 0);
    const saving = total !== null && Number.isSafeInteger(reference) ? Math.max(0, reference - total) : 0;
    return { reference: saving > 0 ? reference : null, saving };
  }
  function safeUrl(value, base) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try { const u = new URL(value, base); return /^https?:$/.test(u.protocol) ? u.href : null; }
    catch (_) { return null; }
  }
  function createQueue() {
    let tail = Promise.resolve();
    return function enqueue(task) {
      const next = tail.then(task);
      tail = next.catch(() => {});
      return next;
    };
  }
  class CartError extends Error {
    constructor(message, uncertain) { super(message); this.name = 'CartError'; this.uncertain = !!uncertain; }
  }
  function validCart(cart) {
    return !!cart && Array.isArray(cart.items) && Number.isFinite(cart.total_price) && Number.isFinite(cart.item_count);
  }
  function createCartClient(fetcher, root, strings, onState, options) {
    const words = Object.assign({}, DEFAULTS, strings);
    const enqueue = createQueue();
    const storage = options?.storage;
    const storageKey = 'softhour:pending-cart-write:' + route(root, 'cart');
    let operation = null;
    try {
      const saved = JSON.parse(storage?.getItem(storageKey) || 'null');
      if (saved?.version === 1 && ['pending', 'unknown', 'accepted', 'rejected'].includes(saved.outcome)) operation = { outcome: saved.outcome };
    } catch (_) { /* Restricted storage does not prevent ordinary shopping. */ }
    const state = { cart: null, busy: false, unresolved: !!operation, error: operation ? words.reviewNeeded : '' };
    const emit = () => { if (onState) onState(Object.assign({}, state)); };
    function persistOperation() {
      try {
        if (operation) storage?.setItem(storageKey, JSON.stringify({ version: 1, outcome: operation.outcome }));
        else storage?.removeItem(storageKey);
      } catch (_) { /* In-memory protection remains active if storage is unavailable. */ }
    }
    async function request(endpoint, body, write) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      let timer;
      function settled(outcome) {
        if (!write) return;
        write.outcome = outcome;
        if (operation !== write) return;
        persistOperation();
        // A late definitive POST response can resolve the intent. A GET alone cannot.
        if (state.unresolved && (outcome === 'accepted' || outcome === 'rejected')) refresh();
      }
      async function perform() {
        try {
          const response = await fetcher(route(root, endpoint), {
            method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
            headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
            ...(controller ? { signal: controller.signal } : {}),
            ...(body ? { body: JSON.stringify(body) } : {})
          });
          let data;
          try { data = await response.json(); }
          catch (_) { throw new CartError(words.cartError, !!body); }
          if (!response.ok) {
            const description = typeof data.description === 'string' ? data.description : (typeof data.message === 'string' ? data.message : words.cartError);
            throw new CartError(description, !!body && response.status >= 500);
          }
          settled('accepted');
          return data;
        } catch (error) {
          const known = error instanceof CartError ? error : new CartError(words.cartError, !!body);
          settled(known.uncertain ? 'unknown' : 'rejected');
          throw known;
        }
      }
      try {
        return await Promise.race([perform(), new Promise((_, reject) => {
          timer = setTimeout(() => {
            // Aborting a write does not cancel its server commit. Keep its response observable.
            if (!body) controller?.abort();
            reject(new CartError(words.cartError, !!body));
          }, options?.timeoutMs || 15000);
        })]);
      } finally { clearTimeout(timer); }
    }
    async function read() {
      const confirmedWrite = operation && (operation.outcome === 'accepted' || operation.outcome === 'rejected') ? operation : null;
      const cart = await request('cart.js');
      if (!validCart(cart)) throw new CartError(words.cartReadError, false);
      state.cart = cart;
      if (confirmedWrite && operation === confirmedWrite) {
        operation = null; persistOperation(); state.unresolved = false;
      }
      return cart;
    }
    function refresh() {
      return enqueue(async () => {
        state.busy = true; emit();
        try {
          await read(); state.error = state.unresolved ? words.reviewNeeded : '';
          return { ok: true, cart: state.cart, unresolved: state.unresolved, error: state.error };
        } catch (_) {
          state.error = state.unresolved ? words.reviewNeeded : words.cartReadError;
          return { ok: false, cart: state.cart, error: state.error, unresolved: state.unresolved };
        } finally { state.busy = false; emit(); }
      });
    }
    function mutate(endpoint, body, check) {
      return enqueue(async () => {
        if (state.unresolved) {
          state.error = words.reviewNeeded; emit();
          return { ok: false, cart: state.cart, error: state.error, uncertain: true };
        }
        state.busy = true; state.error = ''; emit();
        let accepted = false, write = null;
        try {
          if (check) check(state.cart);
          write = { outcome: 'pending' }; operation = write; persistOperation();
          await request(endpoint, body, write);
          accepted = true;
          await read();
          return { ok: true, cart: state.cart };
        } catch (error) {
          const uncertain = accepted || !!error.uncertain;
          // Preserve the write latch even when a read returns a temporarily empty cart.
          if (uncertain) state.unresolved = true;
          else if (operation === write) { operation = null; persistOperation(); }
          let reconciled = false;
          try { await read(); reconciled = true; } catch (_) {}
          if (accepted && reconciled && !state.unresolved) return { ok: true, cart: state.cart };
          state.error = accepted ? words.acceptedUnread : uncertain ? words.uncertain : error.message || words.cartError;
          return { ok: false, cart: state.cart, uncertain, accepted, error: state.error };
        } finally { state.busy = false; emit(); }
      });
    }
    return {
      state, refresh,
      add(id, quantity, extra) {
        if (!/^\d+$/.test(String(id)) || parseQuantity(quantity) == null) return Promise.resolve({ ok: false, error: words.invalidQuantity });
        const item = Object.assign({}, extra || {}, { id: String(id), quantity: Number(quantity) });
        return mutate('cart/add.js', { items: [item] });
      },
      addItems(entries) {
        const items = cartItems(entries);
        if (!items) return Promise.resolve({ ok: false, error: words.invalidQuantity });
        return mutate('cart/add.js', { items });
      },
      change(key, quantity) {
        if (typeof key !== 'string' || !key || parseQuantity(quantity, { min: 0 }) == null) return Promise.resolve({ ok: false, error: words.invalidQuantity });
        return mutate('cart/change.js', { id: key, quantity: Number(quantity) }, cart => {
          if (cart && !cart.items.some(item => item.key === key)) throw new CartError(words.missingLine, false);
        });
      }
    };
  }
  function mount(win) {
    const doc = win.document;
    let supplied = {};
    try { supplied = JSON.parse(doc.querySelector('#sf-config')?.textContent || '{}'); } catch (_) { /* Native forms remain usable with the defaults. */ }
    const config = Object.assign({ rootUrl: win.Shopify?.routes?.root || '/', currency: 'USD', locale: doc.documentElement.lang || 'en-US', allowCatalogImages: false }, supplied);
    const words = Object.assign({}, DEFAULTS, config.strings);
    const format = value => money(value, client.state.cart?.currency || config.currency, config.locale);
    const controllers = new Set();
    const mounted = new WeakSet();
    let renderedCart = null, previousFocus = null, pendingCartFocus = null;
    let sessionStorage;
    try { sessionStorage = win.sessionStorage; } catch (_) {}
    const getDialog = () => doc.querySelector('dialog[data-sf-cart]');
    const textAll = (scope, selector, value) => scope.querySelectorAll(selector).forEach(el => { el.textContent = value; });
    const client = createCartClient(win.fetch.bind(win), config.rootUrl, words, state => {
      controllers.forEach(controller => { if (controller.root.isConnected) controller.updateButton(); else { controller.destroy?.(); controllers.delete(controller); } });
      const dialog = getDialog();
      if (state.busy && dialog?.open && doc.activeElement?.dataset.sfCartKey && !pendingCartFocus) {
        pendingCartFocus = { key: doc.activeElement.dataset.sfCartKey, action: doc.activeElement.dataset.sfCartAction, parkedOn: dialog.querySelector('[data-sf-cart-close]') };
        pendingCartFocus.parkedOn?.focus({ preventScroll: true });
      }
      if (state.unresolved) textAll(doc, '[data-sf-error]', state.error || words.reviewNeeded);
      textAll(doc, '[data-sf-cart-error]', state.error);
      if (state.cart) {
        textAll(doc, '[data-sf-cart-count]', String(state.cart.item_count));
        textAll(doc, '[data-sf-cart-total]', format(state.cart.total_price));
        const summary = cartPriceSummary(state.cart, config.compareCatalog, state.cart.currency);
        doc.querySelectorAll('[data-sf-cart-saving]').forEach(el => { el.hidden = !(summary.saving > 0); });
        textAll(doc, '[data-sf-cart-saving-amount]', format(summary.saving));
        textAll(doc, '[data-sf-cart-reference-amount]', summary.reference === null ? '' : format(summary.reference));
        doc.querySelectorAll('[data-sf-cart-reference]').forEach(el => { el.hidden = summary.reference === null; });
        doc.querySelectorAll('[data-sf-cart-summary], [data-sf-cart-campaign], [data-sf-cart-people]').forEach(el => { el.hidden = state.cart.items.length === 0; });
        doc.querySelectorAll('[data-sf-cart-shipping]').forEach(el => { el.hidden = state.cart.items.length === 0; });
        doc.querySelectorAll('[data-sf-cart-empty]').forEach(el => { el.hidden = state.cart.items.length > 0; });
        if (dialog && state.cart !== renderedCart) { renderItems(dialog, state.cart); renderedCart = state.cart; }
      }
      if (dialog) {
        dialog.setAttribute('aria-busy', String(state.busy));
        dialog.querySelectorAll('[data-sf-cart-line] button, [data-sf-cart-line] input').forEach(el => { el.disabled = state.busy || state.unresolved; });
        dialog.querySelectorAll('[name="checkout"]').forEach(el => { el.disabled = state.busy || state.unresolved || (state.cart ? state.cart.items.length === 0 : false); });
        if (!state.busy && pendingCartFocus) {
          if (dialog.open && !state.unresolved && doc.activeElement === pendingCartFocus.parkedOn) {
            const target = Array.from(dialog.querySelectorAll('[data-sf-cart-key]')).find(el => el.dataset.sfCartKey === pendingCartFocus.key && el.dataset.sfCartAction === pendingCartFocus.action);
            (target || dialog.querySelector('[data-sf-cart-close]'))?.focus({ preventScroll: true });
          }
          pendingCartFocus = null;
        }
      }
    }, { storage: sessionStorage });
    function node(tag, className, text) {
      const el = doc.createElement(tag);
      if (className) el.className = className;
      if (text != null) el.textContent = String(text);
      return el;
    }
    function renderItems(dialog, cart) {
      const container = dialog.querySelector('[data-sf-cart-items]');
      if (!container) return;
      const active = doc.activeElement;
      const restore = active?.dataset.sfCartKey ? { key: active.dataset.sfCartKey, action: active.dataset.sfCartAction } : null;
      const fragment = doc.createDocumentFragment();
      cart.items.forEach(item => {
        const row = node('article', 'sf-cart-line'); row.dataset.sfCartLine = ''; row.dataset.sfVariant = item.variant_title || '';
        const imageUrl = safeUrl(item.featured_image?.url || item.image, win.location.href);
        if (imageUrl && config.allowCatalogImages === true) {
          const image = node('img', 'sf-cart-line__image'); image.src = imageUrl;
          image.alt = item.featured_image?.alt || item.product_title || item.title || ''; image.width = 88; image.height = 110; image.loading = 'lazy';
          const frame = node('div', 'sf-cart-line__media'); frame.append(image); row.append(frame);
        }
        const details = node('div', 'sf-cart-line__details');
        const productUrl = safeUrl(item.url, win.location.href);
        const title = node(productUrl ? 'a' : 'span', 'sf-cart-line__title', item.product_title || item.title);
        if (productUrl) title.href = productUrl;
        details.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') details.append(node('p', 'sf-cart-line__variant', item.variant_title));
        const publicProperties = Array.isArray(item.properties) ? item.properties : Object.entries(item.properties || {});
        publicProperties.forEach(entry => {
          if (!Array.isArray(entry) || entry.length < 2) return;
          const [name, value] = entry;
          if (typeof name !== 'string' || name.startsWith('_') || value == null || typeof value === 'object' || String(value).trim() === '') return;
          details.append(node('p', 'sf-cart-line__property', name + ': ' + String(value)));
        });
        const planName = item.selling_plan_allocation?.selling_plan?.name;
        if (typeof planName === 'string' && planName.trim()) details.append(node('p', 'sf-cart-line__plan', words.plan + ': ' + planName));
        const prices = node('div', 'sf-cart-line__prices');
        const price = cartLinePriceState(item, config.compareCatalog, cart.currency);
        if (price.discounted) prices.append(node('s', '', format(price.original)));
        if (price.free) {
          prices.append(node('strong', 'sf-cart-line__pill sf-cart-line__pill--free', words.free));
          prices.append(node('span', 'sf-cart-visually-hidden', format(0)));
        } else {
          prices.append(node('strong', price.discounted ? 'sf-cart-line__amount sf-cart-line__amount--deal' : 'sf-cart-line__amount', price.final === null ? words.unavailablePrice : format(price.final)));
          if (price.saving > 0) prices.append(node('span', 'sf-cart-line__pill', words.save + ' ' + format(price.saving)));
        }
        price.allocations.forEach(discount => {
          details.append(node('span', 'sf-cart-line__discount', (discount.discount_application?.title || words.save) + ' −' + format(discount.amount)));
        });
        const controls = node('div', 'sf-cart-line__quantity');
        function button(action, label, content) {
          const el = node('button', 'sf-cart-line__' + action, content); el.type = 'button';
          el.dataset.sfCartKey = item.key; el.dataset.sfCartAction = action; el.setAttribute('aria-label', label + ': ' + (item.product_title || item.title));
          return el;
        }
        controls.append(button('decrease', words.decrease, '−'));
        const input = node('input'); input.type = 'number'; input.min = '1'; input.step = '1'; input.value = String(item.quantity);
        input.inputMode = 'numeric'; input.dataset.sfCartKey = item.key; input.dataset.sfCartAction = 'quantity';
        input.setAttribute('aria-label', words.quantity + ': ' + (item.product_title || item.title)); controls.append(input);
        controls.append(button('increase', words.increase, '+'));
        details.append(controls, button('remove', words.remove, words.remove));
        row.append(details, prices); fragment.append(row);
      });
      container.replaceChildren(fragment);
      if (restore) {
        const replacement = Array.from(container.querySelectorAll('[data-sf-cart-key]')).find(el => el.dataset.sfCartKey === restore.key && el.dataset.sfCartAction === restore.action);
        (replacement || dialog.querySelector('[data-sf-cart-close]'))?.focus({ preventScroll: true });
      }
    }
    function showCart() {
      const dialog = getDialog();
      if (!dialog || typeof dialog.showModal !== 'function') { win.location.assign(route(config.rootUrl, 'cart')); return; }
      if (!dialog.open) { previousFocus = doc.activeElement; dialog.showModal(); }
      doc.documentElement.classList.add('sf-cart-is-open');
    }
    function closeCart() {
      const dialog = getDialog();
      if (dialog?.open) dialog.close();
      doc.documentElement.classList.remove('sf-cart-is-open');
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    }
    function initProduct(root) {
      if (mounted.has(root)) return;
      let sectionStrings = {};
      try { sectionStrings = JSON.parse(root.querySelector('[data-sf-product-strings]')?.textContent || '{}'); } catch (_) {}
      const productWords = Object.assign({}, words, sectionStrings);
      let product;
      try { product = JSON.parse(root.querySelector('[data-sf-product-json]')?.textContent || 'null'); } catch (_) { return; }
      const form = root.querySelector('form[data-sf-form]'), canonical = root.querySelector('select[data-sf-variant-select]');
      if (!product || !Array.isArray(product.variants) || !product.variants.length || !form || !canonical) return;
      if (product.variants.some(v => !/^\d+$/.test(String(v.id)) || !Number.isFinite(Number(v.price)))) return;
      const controls = Array.from(root.querySelectorAll('select[data-sf-option-index], input[data-sf-option-index]'));
      const optionCount = variantOptions(product.variants[0]).length;
      const namedColorIndex = (product.options || []).findIndex(option => /^(color|colour|couleur)$/i.test(typeof option === 'string' ? option : option.name));
      const declaredColorIndex = Number(root.dataset.sfColorOptionIndex);
      const colorIndex = root.dataset.sfColorOptionIndex !== undefined && Number.isInteger(declaredColorIndex) ? declaredColorIndex : namedColorIndex;
      if (product.variants.length > 1 && Array.from({ length: optionCount }, (_, index) => index).some(index => !controls.some(control => Number(control.dataset.sfOptionIndex) === index))) return;
      const quantityInput = form.querySelector('[data-sf-quantity]');
      const offerControls = Array.from(form.querySelectorAll('input[data-sf-offer]'));
      const secondSelect = form.querySelector('[data-sf-second-variant]');
      const secondControls = Array.from(form.querySelectorAll('select[data-sf-second-option-index], input[data-sf-second-option-index]'));
      const offersEnabled = root.dataset.sfOffersEnabled === 'true';
      if (offersEnabled && (!secondSelect || !['1', '2'].every(value => offerControls.some(el => el.value === value)))) return;
      const canUpdateUrl = root.dataset.sfUpdateUrl !== 'false' && (root.dataset.sfUpdateUrl === 'true' || win.location.pathname.replace(/\/$/, '').endsWith('/products/' + product.handle));
      let variant = product.variants.find(v => String(v.id) === canonical.value) || null;
      if (canUpdateUrl) {
        const requested = new URL(win.location.href).searchParams.get('variant');
        variant = product.variants.find(v => String(v.id) === requested) || variant;
      }
      if (!variant) return; // Keep the native selector if its initial state cannot be understood.
      let secondVariant = variant, selectedOffer = 1, mediaVariant = variant;
      let adding = false, quantity = 1, reviewing = false;
      const setError = message => textAll(root, '[data-sf-error]', message || '');
      function readQuantity() {
        if (offersEnabled) return selectedOffer;
        return quantityInput ? parseQuantity(quantityInput.value, { min: quantityInput.min || 1, max: quantityInput.max, step: quantityInput.step || 1 }) : 1;
      }
      function selection() {
        const chosen = offersEnabled && selectedOffer === 2 ? [variant, secondVariant] : [variant];
        if (chosen.some(item => !item)) return { price: null, available: false, exists: false };
        const price = offersEnabled ? { unit: Number(variant.price), total: chosen.reduce((sum, item) => sum + Number(item.price), 0), compare: null, saving: 0 } : priceState(variant, quantity);
        return { price, available: chosen.every(item => item.available), exists: true };
      }
      function syncControls() {
        const selected = variantOptions(variant);
        controls.forEach(el => {
          const value = selected[Number(el.dataset.sfOptionIndex)];
          if (el.type === 'radio') el.checked = el.value === value; else el.value = value ?? '';
        });
      }
      function updateButton() {
        const enabled = root.dataset.commerceEnabled === 'true';
        const { price, available, exists } = selection();
        let label = productWords.addToCart + (price ? ' · ' + format(price.total) : '');
        if (!enabled) label = productWords.comingSoon;
        else if (!exists) label = productWords.unavailable;
        else if (!available) label = productWords.soldOut;
        else if (quantity == null) label = productWords.invalidQuantity;
        else if (adding) label = productWords.adding;
        if (client.state.unresolved) { label = productWords.reviewBag; setError(productWords.reviewNeeded); reviewing = true; }
        else if (reviewing) { setError(''); reviewing = false; }
        root.querySelectorAll('[data-sf-submit]').forEach(button => {
          button.disabled = adding || client.state.busy || (!client.state.unresolved && (!enabled || !exists || !available || quantity == null));
          button.setAttribute('aria-busy', String(adding));
          const compactLabel = button.dataset.sfSubmitCompact !== undefined && enabled && available && quantity != null && !adding && !client.state.unresolved;
          const text = button.querySelector('[data-sf-submit-label]') || button; text.textContent = compactLabel ? productWords.addToCart : label;
        });
      }
      function allowsMedia(element) {
        const mediaColor = (element.dataset.color || '').trim().toLowerCase();
        const selectedColor = mediaVariant && colorIndex >= 0 ? (variantOptions(mediaVariant)[colorIndex] || '').trim().toLowerCase() : '';
        return !mediaColor || mediaColor === selectedColor;
      }
      function activateMedia(id) {
        const target = Array.from(root.querySelectorAll('[data-sf-media]')).find(el => el.dataset.mediaId === String(id));
        if (id != null && (!target || !allowsMedia(target))) return;
        const hasMedia = Array.from(root.querySelectorAll('[data-sf-media]')).some(el => el.dataset.mediaId === String(id));
        const gallery = root.querySelector('[data-sf-gallery-container]');
        if (gallery) {
          const showEditor = gallery.dataset.sfGalleryEditor === 'true';
          gallery.hidden = !hasMedia && !showEditor;
          root.classList[hasMedia || showEditor ? 'remove' : 'add']('sf-product--without-media');
        }
        root.querySelectorAll('[data-sf-media]').forEach(el => {
          const active = el.dataset.mediaId === String(id); el.hidden = !active;
          if (!active) el.querySelectorAll('video').forEach(video => video.pause());
        });
        root.querySelectorAll('[data-sf-thumb]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.mediaId === String(id))));
        root.querySelectorAll('[data-sf-gallery-empty]').forEach(el => { el.hidden = !!id; });
      }
      function updateMedia() {
        const nativeGallery = root.querySelector('[data-sf-native-gallery]');
        if (nativeGallery) {
          nativeGallery.dataset.selectedColor = mediaVariant && colorIndex >= 0 ? (variantOptions(mediaVariant)[colorIndex] || '').trim().toLowerCase() : '';
          nativeGallery.dispatchEvent(new CustomEvent('sf:variant-media'));
          return;
        }
        const media = Array.from(root.querySelectorAll('[data-sf-media]'));
        const featuredId = mediaVariant?.featured_media?.id || mediaVariant?.featured_image?.id;
        const featured = media.find(el => allowsMedia(el) && (String(el.dataset.mediaId) === String(featuredId) || (el.dataset.imageId && String(el.dataset.imageId) === String(mediaVariant?.featured_image?.id))));
        const selected = featured || media.find(el => el.dataset.color && allowsMedia(el)) || media.find(el => !el.dataset.color);
        root.querySelectorAll('[data-sf-thumb]').forEach(el => {
          const target = media.find(frame => frame.dataset.mediaId === el.dataset.mediaId);
          el.hidden = !target || !allowsMedia(el) || !allowsMedia(target);
        });
        const visibleThumbs = Array.from(root.querySelectorAll('[data-sf-thumb]')).filter(el => !el.hidden).length;
        root.querySelectorAll('[data-sf-thumbnails]').forEach(el => { el.hidden = visibleThumbs <= 1; });
        activateMedia(selected?.dataset.mediaId || null);
      }
      function update(updateUrl, mediaChanged = true) {
        quantity = readQuantity();
        canonical.value = variant ? String(variant.id) : '';
        const { price } = selection();
        textAll(root, '[data-sf-price]', price ? format(price.total) : productWords.unavailablePrice);
        textAll(root, '[data-sf-unit-price]', price ? format(price.unit) : productWords.unavailablePrice);
        textAll(root, '[data-sf-selected-quantity]', quantity == null ? productWords.unavailablePrice : String(quantity));
        root.querySelectorAll('[data-sf-compare-price]').forEach(el => { el.textContent = price?.compare ? format(price.compare) : ''; el.hidden = !price?.compare; });
        root.querySelectorAll('[data-sf-saving]').forEach(el => { el.textContent = price?.saving ? productWords.save + ' ' + format(price.saving) : ''; el.hidden = !price?.saving; });
        root.querySelectorAll('[data-sf-selected-option]').forEach(el => {
          const index = Number(el.dataset.sfSelectedOption || el.dataset.sfOptionIndex || 0);
          el.textContent = variant ? variantOptions(variant)[index] || '' : '';
        });
        if (offersEnabled) {
          if (quantityInput) quantityInput.value = String(selectedOffer);
          offerControls.forEach(el => { el.checked = Number(el.value) === selectedOffer; });
          secondSelect.value = secondVariant ? String(secondVariant.id) : '';
          if (secondVariant) secondControls.forEach(el => {
            const value = variantOptions(secondVariant)[Number(el.dataset.sfSecondOptionIndex)];
            if (el.type === 'radio') el.checked = el.value === value; else el.value = value ?? '';
          });
          root.querySelectorAll('[data-sf-second-selected-option]').forEach(el => { el.textContent = secondVariant ? variantOptions(secondVariant)[Number(el.dataset.sfSecondSelectedOption)] || '' : ''; });
          root.querySelectorAll('[data-sf-duo-colors]').forEach(el => { el.hidden = selectedOffer !== 2; });
          root.querySelectorAll('[data-sf-first-label]').forEach(el => { el.hidden = selectedOffer !== 2; });
          textAll(root, '[data-sf-offer-price="1"]', variant ? format(Number(variant.price)) : productWords.unavailablePrice);
          textAll(root, '[data-sf-offer-price="2"]', variant && secondVariant ? format(Number(variant.price) + Number(secondVariant.price)) : productWords.unavailablePrice);
        }
        const descriptions = offersEnabled && selectedOffer === 2 ? [variant, secondVariant] : [variant];
        textAll(root, '[data-sf-selection-summary]', descriptions.map(item => item && root.classList.contains('sf-apparel-buy') ? variantOptions(item).join(' / ') : item && colorIndex >= 0 ? variantOptions(item)[colorIndex] : item?.title || product.title || '').join(' + '));
        updateButton(); if (mediaChanged) updateMedia();
        if (updateUrl && canUpdateUrl) {
          const url = new URL(win.location.href);
          if (variant) url.searchParams.set('variant', String(variant.id)); else url.searchParams.delete('variant');
          win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);
        }
      }
      syncControls(); update(false);
      controls.forEach(control => control.addEventListener('change', () => {
        const count = variantOptions(product.variants[0]).length;
        const selected = Array.from({ length: count }, (_, index) => {
          const matching = controls.filter(el => Number(el.dataset.sfOptionIndex) === index);
          return matching.find(el => el.type !== 'radio' || el.checked)?.value ?? '';
        });
        variant = resolveVariant(product.variants, selected); mediaVariant = variant; setError(''); update(true);
      }));
      canonical.addEventListener('change', () => { variant = product.variants.find(v => String(v.id) === canonical.value) || null; mediaVariant = variant; if (variant) syncControls(); setError(''); update(true); });
      if (offersEnabled) {
        secondControls.forEach(control => control.addEventListener('change', () => {
          const selected = Array.from({ length: optionCount }, (_, index) => {
            const matching = secondControls.filter(el => Number(el.dataset.sfSecondOptionIndex) === index);
            return matching.find(el => el.type !== 'radio' || el.checked)?.value ?? '';
          });
          secondVariant = resolveVariant(product.variants, selected); mediaVariant = secondVariant; setError(''); update(false);
        }));
        offerControls.forEach(control => control.addEventListener('change', () => {
          if (!control.checked || !['1', '2'].includes(control.value)) return;
          selectedOffer = Number(control.value); mediaVariant = variant; setError(''); update(false);
        }));
        secondSelect.addEventListener('change', () => {
          secondVariant = product.variants.find(item => String(item.id) === secondSelect.value) || null;
          mediaVariant = secondVariant; setError(''); update(false);
        });
      }
      quantityInput?.addEventListener('input', () => { quantity = readQuantity(); setError(quantity == null ? productWords.invalidQuantity : ''); update(false, false); });
      root.addEventListener('click', event => {
        const thumb = event.target.closest('[data-sf-thumb]');
        if (!thumb || !root.contains(thumb) || thumb.hidden) return;
        event.preventDefault(); activateMedia(thumb.dataset.mediaId);
      });
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (adding || client.state.busy) return;
        if (client.state.unresolved) { setError(words.reviewNeeded); win.location.assign(route(config.rootUrl, 'cart')); return; }
        quantity = readQuantity();
        if (root.dataset.commerceEnabled !== 'true' || !selection().available) { updateButton(); return; }
        if (quantity == null) { setError(productWords.invalidQuantity); updateButton(); return; }
        const extra = {}, data = new win.FormData(form), properties = {};
        for (const [name, value] of data.entries()) {
          const match = name.match(/^properties\[([^\]]+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        if (Object.keys(properties).length) extra.properties = properties;
        if (data.get('selling_plan')) extra.selling_plan = String(data.get('selling_plan'));
        adding = true; setError(''); updateButton();
        const entries = offersEnabled && selectedOffer === 2 ? [variant, secondVariant].map(item => Object.assign({}, extra, { id: item.id, quantity: 1 })) : [Object.assign({}, extra, { id: variant.id, quantity })];
        const result = await client.addItems(entries);
        adding = false; setError(result.ok ? '' : result.error); updateButton();
        if (result.ok || result.uncertain || result.accepted) showCart();
      });
      let stickyObserver = null, stickyFrame = null;
      const sticky = root.querySelector('[data-sf-sticky]'), mainButton = form.querySelector('[data-sf-submit]');
      function paintSticky() {
        stickyFrame = null;
        if (!root.isConnected || !sticky || !mainButton) return;
        const show = mainButton.getBoundingClientRect().bottom < 0;
        if (show) doc.querySelectorAll('[data-sf-sticky]').forEach(other => { if (other !== sticky) other.hidden = true; });
        sticky.hidden = !show;
      }
      function scheduleSticky() {
        if (stickyFrame === null) stickyFrame = win.requestAnimationFrame(paintSticky);
      }
      if (sticky && mainButton) {
        if (typeof win.IntersectionObserver === 'function') {
          stickyObserver = new win.IntersectionObserver(scheduleSticky);
          stickyObserver.observe(mainButton);
        }
        // A direct jump can skip the viewport without crossing an observed threshold.
        win.addEventListener('scroll', scheduleSticky, { passive: true });
        win.addEventListener('resize', scheduleSticky);
        scheduleSticky();
      }
      controllers.add({ root, updateButton,
        selectColor(id) {
          const next = product.variants.find(item => String(item.id) === String(id));
          if (!next) return false;
          if (adding || client.state.busy) return true;
          variant = next; mediaVariant = next; syncControls(); setError(''); update(true);
          root.scrollIntoView({ behavior: win.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
          controls.find(control => control.checked)?.focus({ preventScroll: true });
          return true;
        },
        destroy() {
          stickyObserver?.disconnect();
          win.removeEventListener('scroll', scheduleSticky);
          win.removeEventListener('resize', scheduleSticky);
          if (stickyFrame !== null) win.cancelAnimationFrame(stickyFrame);
          if (sticky) sticky.hidden = true;
        }
      });
      mounted.add(root); root.classList.add('is-enhanced');
    }
    function init(scope) {
      if (scope.matches?.('[data-sf-product]')) initProduct(scope);
      scope.querySelectorAll('[data-sf-product]').forEach(initProduct);
      const dialog = getDialog();
      if (dialog && !mounted.has(dialog)) {
        dialog.addEventListener('close', () => { doc.documentElement.classList.remove('sf-cart-is-open'); if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true }); });
        dialog.addEventListener('click', event => { if (event.target === dialog) closeCart(); });
        mounted.add(dialog); renderedCart = null;
      }
    }
    doc.addEventListener('click', event => {
      const colorLink = event.target.closest('[data-sf-color-choice]');
      if (colorLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!event.button || event.button === 0)) {
        for (const controller of controllers) {
          if (controller.selectColor?.(colorLink.dataset.sfColorChoice)) { event.preventDefault(); return; }
        }
      }
      if (event.target.closest('[data-sf-cart-open]')) { event.preventDefault(); showCart(); client.refresh(); return; }
      if (event.target.closest('[data-sf-cart-close]')) { event.preventDefault(); closeCart(); return; }
      const action = event.target.closest('button[data-sf-cart-action]');
      if (!action || client.state.busy) return;
      const item = client.state.cart?.items.find(line => line.key === action.dataset.sfCartKey);
      if (!item) return;
      event.preventDefault();
      const quantity = action.dataset.sfCartAction === 'remove' ? 0 : item.quantity + (action.dataset.sfCartAction === 'increase' ? 1 : -1);
      client.change(item.key, quantity);
    });
    doc.addEventListener('change', event => {
      const input = event.target.closest('input[data-sf-cart-action="quantity"]');
      if (!input || client.state.busy) return;
      const quantity = parseQuantity(input.value, { min: 1 });
      if (quantity == null) {
        textAll(doc, '[data-sf-cart-error]', words.invalidQuantity);
        input.value = String(client.state.cart?.items.find(item => item.key === input.dataset.sfCartKey)?.quantity || 1); return;
      }
      client.change(input.dataset.sfCartKey, quantity);
    });
    doc.addEventListener('shopify:section:load', event => { init(event.target); if (getDialog()?.open) client.refresh(); });
    doc.addEventListener('shopify:section:unload', event => {
      controllers.forEach(controller => {
        if (event.target === controller.root || event.target.contains(controller.root)) { controller.destroy?.(); controllers.delete(controller); mounted.delete(controller.root); }
      });
    });
    win.addEventListener('pageshow', event => { if (event.persisted) { controllers.forEach(controller => controller.updateButton()); client.refresh(); } });
    init(doc);
    return { client, init, showCart, closeCart };
  }
  return { route, parseQuantity, resolveVariant, variantOptions, priceState, cartItems, money, explicitMoney, linePriceState, cartLinePriceState, cartPriceSummary, safeUrl, createQueue, createCartClient, mount };
});
